import { Constants } from '@/constants';
import { useFeatureFlags, usePermissions } from '@/composables/permissions';
import { ContactType } from '@/generated/graphql';
import { FeatureFlag } from '@/constants/featureFlags';
import { useProjectModifyModalStore } from '@/features/Project';
import { useMfeUtils } from '@/composables';

const { Module, Type, Action, PermissionAction } = Constants;

interface QuickAction {
	title: string;
	action: string | (() => void);
	icon: string;
}

export const useQuickActionItems = () => {
	const { can } = usePermissions();
	const { rootEmit } = useMfeUtils();
	const { isFeatureEnabled } = useFeatureFlags();
	const projectModalStore = useProjectModifyModalStore();

	/**
	 * Get the quick action items for the user with the correct permission checks.
	 */
	const getQuickActionItemsData = (): QuickAction[] => {
		const items: (QuickAction | false)[] = [
			{
				title: 'new_email',
				action: () => {
					rootEmit('show-send-mail-modal', {
						module: 'contacts',
					});
				},
				icon: 'icon-mail-16',
			},
			can(PermissionAction.CREATE, Type.CalendarEvent) && {
				title: 'add_event',
				action: () => {
					rootEmit('show-event-popup', {
						eventType: 'cal',
					});
				},
				icon: `icon-${Module.CALENDAR.icon}-16`,
			},
			can(PermissionAction.CREATE, Type.Task) && {
				title: 'add_task',
				action: () => {
					rootEmit('show-event-popup', {
						eventType: 'task',
					});
				},
				icon: 'icon-check-16',
			},
			can(PermissionAction.CREATE, ContactType.Person) && {
				title: 'add_person',
				action: `/${Module.PERSONS.name}/${Action.MODIFY}`,
				icon: `icon-${Module.PERSONS.icon}-16`,
			},
			can(PermissionAction.CREATE, ContactType.Company) && {
				title: 'add_company',
				action: `/${Module.COMPANIES.name}/${Action.MODIFY}`,
				icon: `icon-${Module.COMPANIES.icon}-16`,
			},
			can(PermissionAction.CREATE, Type.Project) &&
				!isFeatureEnabled(FeatureFlag.PROJECT_MODIFY_POPUP) && {
					title: 'add_project',
					action: `/${Module.PROJECTS.name}/${Action.MODIFY}`,
					icon: `icon-${Module.PROJECTS.icon}-16`,
				},
			can(PermissionAction.CREATE, Type.Project) &&
				isFeatureEnabled(FeatureFlag.PROJECT_MODIFY_POPUP) && {
					title: 'add_project',
					action: () => projectModalStore.open(),
					icon: `icon-${Module.PROJECTS.icon}-16`,
				},
			can(PermissionAction.CREATE, Type.RealEstateObject) && {
				title: 'add_property',
				action: `/${Module.REALESTATE.name}/${Action.MODIFY}`,
				icon: `icon-${Module.REALESTATE.icon}-16`,
			},
			can(PermissionAction.CREATE, Type.Quote) && {
				title: 'add_quote',
				action: `/${Module.QUOTES.name}/${Action.MODIFY}`,
				icon: `icon-${Module.QUOTES.icon}-16`,
			},
			can(PermissionAction.CREATE, Type.Order) && {
				title: 'add_order',
				action: `/${Module.ORDERS.name}/${Action.MODIFY}`,
				icon: `icon-${Module.ORDERS.icon}-16`,
			},
			can(PermissionAction.CREATE, Type.Invoice) && {
				title: 'add_invoice',
				action: `/${Module.INVOICES.name}/${Action.MODIFY}`,
				icon: `icon-${Module.INVOICES.icon}-16`,
			},
			can(PermissionAction.CREATE, Type.Bill) && {
				title: 'add_bill',
				action: `/${Module.BILLS.name}/${Action.MODIFY}`,
				icon: 'icon-paper-coins-16',
			},
			can(PermissionAction.CREATE, Type.PurchaseOrder) && {
				title: 'add_purchase_order',
				action: `/${Module.PURCHASE_ORDERS.name}/${Action.MODIFY}`,
				icon: 'icon-paper-16',
			},
			can(PermissionAction.CREATE, Type.Expense) && {
				title: 'add_expense',
				action: `/${Module.EXPENSES.name}/${Action.MODIFY}`,
				icon: 'icon-paper-coins-16',
			},
		];

		return items.filter((item): item is QuickAction => !!item);
	};

	return {
		getQuickActionItemsData,
	};
};
